import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Eye, EyeOff, Check, X, ShieldCheck } from 'lucide-react';
import './Admin.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const adminFetch = async (endpoint, options = {}) => {
  const token = localStorage.getItem('adminToken');
  if (!token) { window.location.href = '/admin/login'; return null; }
  const res = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...options.headers },
  });
  if (res.status === 401) {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    window.location.href = '/admin/login';
    return null;
  }
  return res;
};

const passwordRules = (pwd) => ({
  length: pwd.length >= 10,
  upper: /[A-Z]/.test(pwd),
  number: /[0-9]/.test(pwd),
  special: /[^A-Za-z0-9]/.test(pwd),
});

const Rule = ({ ok, children, testId }) => (
  <li className={`pwd-rule ${ok ? 'pwd-rule-ok' : 'pwd-rule-bad'}`} data-testid={testId}>
    {ok ? <Check size={14} /> : <X size={14} />}
    <span>{children}</span>
  </li>
);

const AdminChangePassword = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) { navigate('/admin/login'); return; }
  }, [navigate]);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { if (meta.parentNode) meta.parentNode.removeChild(meta); };
  }, []);

  const rules = passwordRules(next);
  const allRulesOk = rules.length && rules.upper && rules.number && rules.special;
  const matchOk = next.length > 0 && next === confirm;
  const canSubmit = current.length > 0 && allRulesOk && matchOk && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!allRulesOk) { setError('Bitte erfuellen Sie alle Passwort-Regeln.'); return; }
    if (!matchOk) { setError('Die neuen Passwoerter stimmen nicht ueberein.'); return; }
    if (current === next) { setError('Das neue Passwort darf nicht mit dem aktuellen Passwort identisch sein.'); return; }

    setLoading(true);
    try {
      const res = await adminFetch('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      if (!res) return;
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = data.detail;
        if (typeof detail === 'string') setError(detail);
        else setError('Passwort konnte nicht geaendert werden.');
        return;
      }
      // Update token so this session stays valid (others are revoked via token_version rotation)
      if (data.token) localStorage.setItem('adminToken', data.token);
      setSuccess(data.message || 'Passwort erfolgreich geaendert.');
      setCurrent(''); setNext(''); setConfirm('');
      setTimeout(() => navigate('/admin/dashboard'), 1800);
    } catch {
      setError('Verbindungsfehler. Bitte erneut versuchen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-dashboard" data-testid="admin-change-password-page">
      <div className="admin-topbar">
        <div className="admin-topbar-left">
          <button className="admin-back-nav" onClick={() => navigate('/admin/dashboard')} data-testid="back-to-dashboard">
            <ArrowLeft size={18} /> Dashboard
          </button>
          <span className="admin-topbar-logo"><ShieldCheck size={18} /> Passwort aendern</span>
        </div>
      </div>

      <div className="admin-main">
        <div className="pwd-change-wrap" data-testid="pwd-change-wrap">
          <form onSubmit={handleSubmit} className="pwd-change-card" data-testid="pwd-change-form">
            <div className="pwd-change-header">
              <div className="pwd-change-icon"><Lock size={22} /></div>
              <div>
                <h2>Sicheres Passwort aendern</h2>
                <p>Nach erfolgreicher Aenderung werden alle anderen Sitzungen automatisch abgemeldet.</p>
              </div>
            </div>

            {error && <div className="admin-login-error" data-testid="pwd-error">{error}</div>}
            {success && <div className="admin-success-flash" style={{ position: 'relative', top: 0, marginBottom: 16 }} data-testid="pwd-success">{success}</div>}

            <div className="admin-input-group">
              <label htmlFor="current">Aktuelles Passwort</label>
              <div className="admin-input-wrapper">
                <Lock size={18} className="admin-input-icon" />
                <input
                  id="current"
                  type={showCurrent ? 'text' : 'password'}
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  placeholder="Aktuelles Passwort"
                  required
                  autoComplete="current-password"
                  data-testid="pwd-current-input"
                />
                <button type="button" className="admin-password-toggle" onClick={() => setShowCurrent(!showCurrent)} tabIndex={-1} data-testid="toggle-show-current">
                  {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="admin-input-group">
              <label htmlFor="new">Neues Passwort</label>
              <div className="admin-input-wrapper">
                <Lock size={18} className="admin-input-icon" />
                <input
                  id="new"
                  type={showNext ? 'text' : 'password'}
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  placeholder="Neues Passwort"
                  required
                  autoComplete="new-password"
                  data-testid="pwd-new-input"
                />
                <button type="button" className="admin-password-toggle" onClick={() => setShowNext(!showNext)} tabIndex={-1} data-testid="toggle-show-new">
                  {showNext ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <ul className="pwd-rules" data-testid="pwd-rules">
                <Rule ok={rules.length} testId="rule-length">Mindestens 10 Zeichen</Rule>
                <Rule ok={rules.upper} testId="rule-upper">Mindestens ein Grossbuchstabe</Rule>
                <Rule ok={rules.number} testId="rule-number">Mindestens eine Zahl</Rule>
                <Rule ok={rules.special} testId="rule-special">Mindestens ein Sonderzeichen</Rule>
              </ul>
            </div>

            <div className="admin-input-group">
              <label htmlFor="confirm">Neues Passwort bestaetigen</label>
              <div className="admin-input-wrapper">
                <Lock size={18} className="admin-input-icon" />
                <input
                  id="confirm"
                  type={showConfirm ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Neues Passwort wiederholen"
                  required
                  autoComplete="new-password"
                  data-testid="pwd-confirm-input"
                />
                <button type="button" className="admin-password-toggle" onClick={() => setShowConfirm(!showConfirm)} tabIndex={-1} data-testid="toggle-show-confirm">
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {confirm.length > 0 && (
                <div className={`pwd-match ${matchOk ? 'pwd-match-ok' : 'pwd-match-bad'}`} data-testid="pwd-match-info">
                  {matchOk ? <><Check size={14} /> Passwoerter stimmen ueberein</> : <><X size={14} /> Passwoerter stimmen nicht ueberein</>}
                </div>
              )}
            </div>

            <button
              type="submit"
              className="admin-login-btn"
              disabled={!canSubmit}
              data-testid="pwd-submit-button"
            >
              {loading ? 'Wird gespeichert...' : 'Passwort aendern'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminChangePassword;
